// Estado do job edvid: carrega GET /jobs/:id e consulta a cada 2,5s enquanto uma
// fase está renderizando (cutting / visual_rendering). O cliente é injetável para
// a página de desenvolvimento rodar o editor inteiro sobre um fixture.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type EdvidJobRecord } from '../../../services/api';
import type { EdlRange, EdvidJobState, EdvidNote, EdvidVisualState, StyleSetup } from '../../../types/videoEdit';
import type { VideoEditorMusicTrack } from '../../../types/videoEditor';

export interface EdvidClient {
  getJob(jobId: string): Promise<EdvidJobRecord>;
  saveCut(jobId: string, ranges: EdlRange[], baseRevision: number): Promise<EdvidJobRecord | null>;
  recut(jobId: string, note?: string): Promise<EdvidJobRecord | null>;
  applyStyle(jobId: string, style: StyleSetup): Promise<EdvidJobRecord | null>;
  saveVisual(jobId: string, props: EdvidVisualState['props'], baseRevision: number): Promise<EdvidJobRecord | null>;
  applyNotes(jobId: string, notes: EdvidNote[]): Promise<EdvidJobRecord | null>;
  listMusic(q?: string): Promise<{ tracks: VideoEditorMusicTrack[]; unavailable?: string | null }>;
  /** volume / liga-desliga da trilha do Instagram (PATCH /jobs/:id/music) — não re-renderiza */
  updateMusic(jobId: string, patch: { volume?: number; enabled?: boolean }): Promise<void>;
  /** URL do entregável mais recente para publicar (getVideoEditorAssetUrl 'final' 'publish') */
  getPublishUrl(jobId: string): Promise<string>;
}

export const apiEdvidClient: EdvidClient = {
  getJob: async id => (await api.getEdvidJob(id)).job,
  saveCut: async (id, ranges, baseRevision) => (await api.saveEdvidCut(id, { ranges, baseRevision })).job || null,
  recut: async (id, note) => (await api.recutEdvid(id, note ? { note } : {})).job || null,
  applyStyle: async (id, style) => (await api.applyEdvidStyle(id, style)).job || null,
  saveVisual: async (id, props, baseRevision) => (await api.saveEdvidVisual(id, { props, baseRevision })).job || null,
  applyNotes: async (id, notes) => (await api.applyEdvidNotes(id, notes)).job || null,
  listMusic: async q => {
    const r = await api.listVideoEditorMusic(q ? { q } : {});
    return { tracks: r.tracks || [], unavailable: r.unavailable };
  },
  updateMusic: async (id, patch) => { await api.updateVideoEditorMusic(id, patch); },
  getPublishUrl: async id => (await api.getVideoEditorAssetUrl(id, 'final', 'publish')).asset.url,
};

const BUSY: Array<EdvidJobState['phase']> = ['cutting', 'visual_rendering'];
export const isBusyPhase = (p?: EdvidJobState['phase']) => !!p && BUSY.includes(p);

export function useEdvidJob(jobId: string, client: EdvidClient) {
  const [job, setJob] = useState<EdvidJobRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  // Depois de uma ação (gerar visual, salvar corte…) segue consultando por um tempo
  // mesmo sem fase ocupada: se a resposta chegou antes de o backend trocar a fase,
  // parar aqui deixava a tela parada para sempre.
  const graceUntil = useRef(0);
  const [graceTick, setGraceTick] = useState(0);

  const edvid = (job?.output?.edvid as EdvidJobState | undefined) || null;
  const busy = isBusyPhase(edvid?.phase) || (!edvid && (job?.status === 'queued' || job?.status === 'running'));

  const reload = useCallback(async () => {
    try {
      const j = await client.getJob(jobId);
      if (!alive.current) return null;
      setJob(j);
      setError(null);
      return j;
    } catch (e: any) {
      if (alive.current) setError(e?.message || 'Erro ao carregar a edição.');
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [client, jobId]);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => { alive.current = false; };
  }, [reload]);

  // polling enquanto renderiza (e na janela de carência depois de uma ação)
  useEffect(() => {
    const inGrace = Date.now() < graceUntil.current;
    if (!busy && !inGrace) return;
    timer.current = setTimeout(() => { void reload().then(() => setGraceTick(t => t + 1)); }, 2500);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [busy, job, reload, graceTick]);

  /** instala o job devolvido por uma mutação (ou recarrega se ela não devolveu nada) */
  const accept = useCallback(async (next: EdvidJobRecord | null) => {
    graceUntil.current = Date.now() + 15000;
    if (next) setJob(next);
    else await reload();
    setGraceTick(t => t + 1);
  }, [reload]);

  return { job, edvid, loading, error, busy, reload, accept };
}
